import is from '@sindresorhus/is';
import { useCallback, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { Translate } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';

import { useReviewJobItemVersion } from '../api/use-review-job-item-version';
import { useConfirm } from '../providers/ConfirmProvider';
import { useTrackWorkflowTransition } from './analytics/use-track-workflow-transition';
import { useConfirmDialogPreference } from './use-confirm-dialog-preference';

type ItemType = 'job-level' | 'job-role';

type ReviewableItem = {
    readonly id: string;
    readonly parentId: string | null;
    readonly versionInfo?: {
        readonly version: number;
    };
};

type UseReviewActionProps = {
    readonly architectureId: string;
    readonly item: ReviewableItem;
    readonly itemType: ItemType;
};

type UseReviewActionResult = {
    readonly handleReview: () => Promise<void>;
};

export const getJobTypeTranslation = (itemType: ItemType, translate: Translate): string => {
    switch (itemType) {
        case 'job-role':
            return translate('pages.job-architecture-v2.job-role');
        case 'job-level':
            return translate('pages.job-architecture-v2.job-level');
    }
};

export const useReviewAction = ({ architectureId, item, itemType }: UseReviewActionProps): UseReviewActionResult => {
    const { translate } = useContext(ApplicationContext);
    const { mutateAsync: reviewJobItemVersion } = useReviewJobItemVersion();
    const { trackWorkflowTransition } = useTrackWorkflowTransition({ architectureId });
    const { dismissDialog, shouldShowDialog } = useConfirmDialogPreference('review');
    const { confirm } = useConfirm();
    const jobTypeTranslation = getJobTypeTranslation(itemType, translate);

    const handleReview = useCallback(async (): Promise<void> => {
        if (shouldShowDialog) {
            const result = await confirm({
                description: translate('pages.job-architecture-v2.mark-job-role-reviewed-content', {
                    jobType: jobTypeTranslation,
                }),
                renderCancelButton: (onCancel) => (
                    <Button size="sm" variant="secondary" onClick={onCancel}>
                        {translate('common.cancel')}
                    </Button>
                ),
                renderConfirmButton: (onConfirm) => (
                    <Button size="sm" variant="default" onClick={onConfirm}>
                        {translate('pages.job-architecture.mark-as-reviewed')}
                    </Button>
                ),
                showDontShowAgainCheckbox: true,
                title: translate('pages.job-architecture-v2.mark-job-role-reviewed-title', {
                    jobType: jobTypeTranslation,
                }),
                type: 'default',
            });

            if (result.dontShowAgain) {
                dismissDialog();
            }
        }

        const versionId = item.versionInfo?.version.toString() ?? '1';

        await reviewJobItemVersion({
            action: 'review',
            architectureId,
            itemId: item.id,
            itemType,
            version: versionId,
        });

        trackWorkflowTransition({
            actionType: 'review_completed',
            fromStatus: 'in_review',
            itemId: item.id,
            itemType,
            parentRoleId: itemType === 'job-level' && !is.null(item.parentId) ? item.parentId : undefined,
            toStatus: 'reviewed',
            versionId,
        });
    }, [
        architectureId,
        confirm,
        dismissDialog,
        item.id,
        item.parentId,
        item.versionInfo?.version,
        itemType,
        jobTypeTranslation,
        reviewJobItemVersion,
        shouldShowDialog,
        trackWorkflowTransition,
        translate,
    ]);

    return { handleReview };
};
